import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  getSocialConfig,
  startSocialConnect,
  SocialConfig,
  SocialProfile,
} from "../../../../database/social/socialApi";

// Postproxy (aggregator) accounts panel. First-party platforms (TikTok,
// LinkedIn, Instagram/Facebook) have their own connect panels; everything else
// a coach schedules — X, Threads, YouTube, Pinterest, Bluesky — publishes
// through the Postproxy profiles connected here. The legacy per-project
// "Schedule to Instagram" modal that used to live in this file is gone:
// scheduling goes through the owned queue (publish/SchedulePostDialog.tsx).

const SmallButton = styled.button`
  appearance: none;
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--color-accent);
    color: var(--color-accent);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(SmallButton)`
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: #fff;

  &:hover:not(:disabled) {
    color: #fff;
    filter: brightness(1.05);
  }
`;

const PanelRoot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  margin-bottom: var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
`;

const PanelLabel = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  margin-right: var(--space-1);
`;

const ProfileChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: var(--radius-pill);
  background: var(--color-surface-subtle);
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--color-text-secondary);

  img {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const Muted = styled.span`
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--color-text-secondary);
`;

const Spacer = styled.span`
  flex: 1 1 auto;
`;

const platformLabel = (p: string): string => (p === "twitter" ? "X" : p);

export const SocialConnectPanel: React.FC<{
  communityId?: string;
}> = ({ communityId }) => {
  const [config, setConfig] = useState<SocialConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError("");
    try {
      const cfg = await getSocialConfig(communityId);
      setConfig(cfg);
    } catch (e: any) {
      setError(e?.message || "Could not load connection status.");
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // When the coach returns from the connect popup to this tab, refresh.
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const handleConnect = async () => {
    if (!communityId) return;
    setBusy(true);
    setError("");
    try {
      const { url } = await startSocialConnect({
        communityId,
        redirectUrl: window.location.href,
      });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      setError(e?.message || "Could not start the connection.");
    } finally {
      setBusy(false);
    }
  };

  if (!communityId) return null;

  const profiles = config?.profiles ?? [];

  return (
    <PanelRoot>
      <PanelLabel>Cross-post accounts</PanelLabel>
      {loading && !config ? (
        <Muted>Checking…</Muted>
      ) : profiles.length ? (
        profiles.map((p: SocialProfile) => (
          <ProfileChip key={p.id}>
            {p.picture ? <img src={p.picture} alt="" /> : null}
            {p.username || p.name || platformLabel(p.platform)}
            <Muted>· {platformLabel(p.platform)}</Muted>
          </ProfileChip>
        ))
      ) : (
        <Muted>
          No cross-post accounts yet. X and other extra channels publish through
          these.
        </Muted>
      )}
      <Spacer />
      {error ? <Muted style={{ color: "var(--color-danger)" }}>{error}</Muted> : null}
      <SmallButton type="button" onClick={refresh} disabled={loading}>
        Refresh
      </SmallButton>
      <PrimaryButton type="button" onClick={handleConnect} disabled={busy}>
        {busy ? "Opening…" : "Connect accounts"}
      </PrimaryButton>
    </PanelRoot>
  );
};
